import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../../api/adminApi';
import type { PromptInfo, StepType, PipelineStepConfig, OperationConfig, OperationsMap, ProviderConfig, ProviderTieredSet, ProviderParallelSet, StepRunConfig, Targeting } from '../../types/settings';
import type { ReasoningEffort, ReasoningSummaryMode, QuestionType } from '@padcanvas/shared';
import { getProviderCapabilities, QUESTION_TYPES, QUESTION_TYPE_LABELS, REGISTRABLE_PROGRAMME_OPTIONS, QUESTION_SUBJECT_OPTIONS, GRADE_OPTIONS, stepTypeFamily, promptTypeLabel, OPERATION_TYPES, OPERATION_TYPE_KEYS, OPERATION_STEP_TYPES, isStepTypeForOperation, RECONCILE_STEP_SOURCES, FEATURE_GATE_NAMES, type OperationType } from '@padcanvas/shared';
import { Modal, Button, IntegerInput, NumberInput } from '@shared-ui/components';
import { CopyPipelineDialog } from './CopyPipelineDialog';
import { applyRunParams, type EditRunParams } from './pipelineRunParams';
import { ConfigHistoryDrawer } from './ConfigHistoryDrawer';
import './Pipelines.css';

// Operation key type for all pipeline operations — canonical list lives in
// @padcanvas/shared (operationTypes.ts). Add a new operation there and it flows
// through here automatically.
type OpKey = OperationType;

/** Coerce a `defaultTier` value (current 0-indexed integer or legacy
 *  `'TierN'` string) into an integer index. Falls back to `1` (the legacy
 *  `'Tier2'` baseline). The runtime clamps to set length. */
function coerceDefaultTier(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) return Math.floor(value);
  if (typeof value === 'string') {
    const m = /^Tier(\d+)$/i.exec(value);
    if (m) return Math.max(0, parseInt(m[1], 10) - 1);
  }
  return 1;
}

/** Render a tier index as a 1-based human label (`0` → `"Tier 1"`). */
function tierLabel(index: number): string {
  return `Tier ${index + 1}`;
}

/** One provider the run can actually reach, with where it sits in the set. */
interface ResolvedProvider {
  /** Where it comes from — "Tier 2" or "Branch", plus "(default)" where it applies. */
  origin: string;
  providerKey: string;
  /** family · model, or a note when the key resolves to nothing. */
  detail: string;
}

/** `family · model` for a provider key, or a warning when it isn't configured —
 *  a set naming a key that no longer exists should read as broken, not blank. */
function describeProvider(key: string, configs: ProviderConfig[]): string {
  const cfg = configs.find(p => p.key === key);
  return cfg ? `${cfg.family} · ${cfg.modelName}` : 'not configured';
}

/**
 * The providers a tier-aware run can actually reach: its default tier plus the
 * tiers escalation can climb to, capped by the set's size. Tiers below the
 * default are never used, so listing the whole set would overstate it.
 *
 * A tier slot may itself be a parallel set, so those expand to their branches.
 */
function resolveTierProviders(
  set: ProviderTieredSet | undefined,
  defaultTier: number,
  maxEscalations: number,
  parallelSets: ProviderParallelSet[],
  configs: ProviderConfig[],
): ResolvedProvider[] {
  const levels = set?.levels ?? [];
  if (levels.length === 0) return [];
  const start = Math.min(Math.max(defaultTier, 0), levels.length - 1);
  const end = Math.min(start + Math.max(maxEscalations, 0), levels.length - 1);
  const out: ResolvedProvider[] = [];
  for (let i = start; i <= end; i++) {
    const slot = levels[i];
    const label = `${tierLabel(i)}${i === start ? ' (default)' : ''}`;
    if (slot?.kind === 'parallel') {
      const nested = parallelSets.find(p => p.name === slot.parallelSet);
      const branches = nested?.branches ?? [];
      if (branches.length === 0) {
        out.push({ origin: label, providerKey: slot.parallelSet, detail: 'parallel set not found' });
      } else {
        for (const b of branches) {
          out.push({
            origin: `${label} · parallel${b.provider === nested?.defaultBranch ? ' (default branch)' : ''}`,
            providerKey: b.provider,
            detail: describeProvider(b.provider, configs),
          });
        }
      }
    } else if (slot?.kind === 'single') {
      out.push({ origin: label, providerKey: slot.provider, detail: describeProvider(slot.provider, configs) });
    }
  }
  return out;
}

/** Every branch of a parallel set — all of them run, so all are listed. */
function resolveParallelProviders(
  set: ProviderParallelSet | undefined,
  configs: ProviderConfig[],
): ResolvedProvider[] {
  return (set?.branches ?? []).map(b => ({
    origin: `Branch${b.provider === set?.defaultBranch ? ' (default)' : ''}`,
    providerKey: b.provider,
    detail: describeProvider(b.provider, configs),
  }));
}

/** Compact form for the runs table, under the set name: the same resolution as
 *  the editor's list, but keys only — the cell has no room for family · model,
 *  and the set name above already says where they come from. */
function ResolvedProviderHint({ providers }: { providers: ResolvedProvider[] }) {
  if (providers.length === 0) return null;
  return (
    <div className="provider-set-resolved">
      {providers.map((p, i) => (
        <span key={`${p.origin}-${p.providerKey}-${i}`} title={`${p.origin} — ${p.detail}`}>
          {p.providerKey}
        </span>
      ))}
    </div>
  );
}

/** Read-only list of the providers a run will actually call. Derived entirely
 *  from the chosen set, so it tracks edits to the set without a save. */
function ResolvedProviderList({ providers, emptyHint }: { providers: ResolvedProvider[]; emptyHint: string }) {
  return (
    <div className="step-editor-resolved">
      <span className="step-editor-resolved-title">Providers used</span>
      {providers.length === 0 ? (
        <span className="step-editor-resolved-empty">{emptyHint}</span>
      ) : (
        <ul>
          {providers.map((p, i) => (
            <li key={`${p.origin}-${p.providerKey}-${i}`}>
              <span className="step-editor-resolved-origin">{p.origin}</span>
              <code>{p.providerKey}</code>
              <span className="step-editor-resolved-detail">{p.detail}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Match a run by id (preferred) or fallback to composite promptId+provider. */
function matchesRun(r: StepRunConfig, runId: string, promptId: string, provider: string): boolean {
  if (runId && r.id) return r.id === runId;
  return r.promptId === promptId && (r.provider ?? '') === provider;
}

/** Provider identity passed to onAddRun — flat, tier-aware, or parallel.
 *  For the flat kind, each param is optional; `undefined` means "don't override
 *  the provider config default" — the run doc stays sparse and the backend
 *  resolves via mergeProviderDefaults at call time. */
type AddRunProvider =
  | {
      kind: 'flat';
      provider: string;
      temperature?: number;
      maxTokens?: number;
      reasoningEffort?: ReasoningEffort;
      thinkingBudgetTokens?: number;
      reasoningSummary?: ReasoningSummaryMode;
      useStrictJsonSchema?: boolean;
    }
  | { kind: 'tier'; providerSet: string }
  | { kind: 'parallel'; parallelSet: string };


/** The backend's per-call default (callProvider.ts PER_CALL_TIMEOUT_MS) —
 *  shown where a step sets none. Prod does not override it with
 *  LLM_CALL_TIMEOUT_MS; if that ever changes, this label must follow. */
const DEFAULT_CALL_TIMEOUT_S = 120;

// Derived from the canonical registry so the list + labels stay in one place.
const ALL_OP_KEYS: OpKey[] = OPERATION_TYPE_KEYS;
const OP_LABELS = Object.fromEntries(
  OPERATION_TYPE_KEYS.map(k => [k, OPERATION_TYPES[k].label]),
) as Record<OpKey, string>;
const OP_DESCRIPTIONS = Object.fromEntries(
  OPERATION_TYPE_KEYS.map(k => [k, OPERATION_TYPES[k].description]),
) as Record<OpKey, string>;
const OP_SINGLE_RUN = Object.fromEntries(
  OPERATION_TYPE_KEYS.map(k => [k, OPERATION_TYPES[k].singleRun]),
) as Record<OpKey, boolean>;

function formatPipelineName(name: string): string {
  if (name === 'mcq-no-llm') return 'MCQ (No LLM)';
  return name.split(/[-_]/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function opsToRecord(opsMap: OperationsMap | undefined): Record<OpKey, OperationConfig | undefined> {
  const result: Record<OpKey, OperationConfig | undefined> = {} as Record<OpKey, OperationConfig | undefined>;
  const map = (opsMap || {}) as Record<string, OperationConfig | undefined>;
  for (const key of ALL_OP_KEYS) {
    result[key] = map[key] || { pipelines: [] };
  }
  return result;
}


/** A provider / provider-set name that opens its own tab.
 *
 *  A button, not an anchor: Providers, Tiered Sets and Parallel Sets are
 *  sibling tabs of this page rather than routes, so there is no URL to point at
 *  and an anchor would be a lie to assistive tech. Falls back to plain text
 *  when no navigation handler is supplied, so the table still renders wherever
 *  this component is used without one.
 */
function ProviderLink({ className, title, onNavigate, children }: {
  className?: string;
  title?: string;
  onNavigate?: () => void;
  children: React.ReactNode;
}) {
  if (!onNavigate) return <span className={className} title={title}>{children}</span>;
  return (
    <button
      type="button"
      className={`${className ?? ''} provider-nav-link`}
      title={title}
      onClick={onNavigate}
    >
      {children}
    </button>
  );
}

export type ProviderNavTarget = 'providers' | 'tiered-sets' | 'parallel-sets';

export function Pipelines({ onNavigate }: {
  /** Jump to the Providers / Tiered Sets / Parallel Sets tab, optionally
   *  focused on one entry. These are sibling tabs of this page, so switching
   *  tab IS the navigation — no route change, no dialog. */
  onNavigate?: (tab: ProviderNavTarget, focus?: string) => void;
} = {}) {
  const queryClient = useQueryClient();
  // Empty until configList loads and the picker effect chooses a tab.
  const [activeConfig, setActiveConfig] = useState<string>('');
  const [operations, setOperations] = useState<Record<OpKey, OperationConfig | undefined>>({} as Record<OpKey, OperationConfig | undefined>);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({ analysis: true, hint: true, textchat: true, realtimeVoice: true, conceptLab: true, learningMoments: true, judgeAnswerEquiv: true, judgeQuality: true } as Record<string, boolean>);
  const [selectedOp, setSelectedOp] = useState<OpKey>('analysis');
  const [showNewConfigInput, setShowNewConfigInput] = useState(false);
  const [newConfigName, setNewConfigName] = useState('');
  const [addingPipelineFor, setAddingPipelineFor] = useState<OpKey | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  // Copy dialog: parent only tracks the source + suggested name; the
  // dialog component owns its own draft/error state. Null when closed.
  const [copyDialog, setCopyDialog] = useState<{ opKey: OpKey; sourcePipelineName: string; suggestedName: string } | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: adminApi.getSettings,
  });

  const { data: prompts } = useQuery({
    queryKey: ['prompts', 'withTemplates'],
    queryFn: () => adminApi.getPrompts({ includeTemplate: true }),
  });

  const { data: providerConfigData } = useQuery({
    queryKey: ['providerConfig'],
    queryFn: adminApi.getProviderConfig,
  });

  const { data: providerSetsData } = useQuery({
    queryKey: ['provider-tiered-sets'],
    queryFn: adminApi.listProviderTieredSets,
  });
  const providerSets: ProviderTieredSet[] = (providerSetsData?.sets || []);

  const { data: parallelSetsData } = useQuery({
    queryKey: ['provider-parallel-sets'],
    queryFn: adminApi.listProviderParallelSets,
  });
  const parallelSets: ProviderParallelSet[] = (parallelSetsData?.sets || []);

  const { data: configList } = useQuery({
    queryKey: ['pipelineConfigs'],
    queryFn: adminApi.listPipelineConfigs,
  });

  const { data: activeConfigDoc } = useQuery({
    queryKey: ['pipelineConfig', activeConfig],
    queryFn: () => adminApi.getPipelineConfig(activeConfig),
    enabled: !!activeConfig,
  });

  const providerConfigs: ProviderConfig[] = providerConfigData?.providers || [];
  const allProviderKeys: string[] = providerConfigs.map(p => p.key as string);
  const activeDefaultName: string | undefined = settings?.defaultPipelineConfig;
  const activeReEvalName: string | undefined = settings?.reEvalPipelineConfig;
  const configNames: string[] = (() => {
    // Active default first (if set and present in the list), then the rest in
    // server order. The list endpoint already filters out soft-deleted docs.
    const all = (configList?.configs || []).map(c => c.name);
    if (activeDefaultName && all.includes(activeDefaultName)) {
      return [activeDefaultName, ...all.filter(n => n !== activeDefaultName)];
    }
    return all;
  })();

  useEffect(() => {
    if (activeConfigDoc) {
      setOperations(opsToRecord(activeConfigDoc.operations));
    }
  }, [activeConfigDoc]);

  // Pick an initial selection once the config list loads (or after a delete).
  useEffect(() => {
    if (activeConfig || configNames.length === 0) return;
    const next = activeDefaultName && configNames.includes(activeDefaultName)
      ? activeDefaultName
      : configNames[0];
    setActiveConfig(next);
  }, [activeConfig, activeDefaultName, configNames]);

  const updateMutation = useMutation({
    mutationFn: ({ name, operations }: { name: string; operations: OperationsMap }) =>
      adminApi.updatePipelineConfig(name, operations),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['pipelineConfig', vars.name] });
      queryClient.invalidateQueries({ queryKey: ['pipelineConfigs'] });
    },
  });

  const createMutation = useMutation({
    mutationFn: ({ name, operations }: { name: string; operations?: OperationsMap }) =>
      adminApi.createPipelineConfig(name, operations),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipelineConfigs'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (name: string) => adminApi.deletePipelineConfig(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipelineConfigs'] });
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: (name: string) => adminApi.updateSettings({ defaultPipelineConfig: name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });

  // Re-eval config: used by /api/reevaluate for student/teacher re-evaluations
  // (typically points at frontier-tier cells). '' clears the setting.
  const setReEvalMutation = useMutation({
    mutationFn: (name: string) => adminApi.updateSettings({ reEvalPipelineConfig: name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });

  const mutation = updateMutation; // alias for isPending checks below

  const saveOperations = useCallback((newOps: typeof operations) => {
    // Auto-stamp any steps or runs missing an id before persisting (those
    // still use UUIDs as instance keys). Pipeline-level `id` is dropped —
    // `pipelineName` is the canonical instance identifier now (see
    // typed-pipelines.md §3.4). Strip it from any pipeline that still
    // carries one from a pre-split Cosmos doc so the field disappears
    // over time without a separate migration.
    for (const op of Object.values(newOps)) {
      if (!op) continue;
      for (const p of op.pipelines) {
        // `id` is no longer on the type (deprecated, keyed by pipelineName now),
        // but old Cosmos docs may still carry one — strip it via a cast so it
        // disappears on save.
        delete (p as unknown as Record<string, unknown>).id;
        for (const s of p.steps) {
          if (!s.id) s.id = crypto.randomUUID();
          for (const r of s.runs) {
            if (!r.id) r.id = crypto.randomUUID();
          }
        }
      }
    }
    const opsMap: OperationsMap = {};
    for (const key of ALL_OP_KEYS) {
      if (newOps[key]) opsMap[key] = newOps[key];
    }
    updateMutation.mutate({ name: activeConfig, operations: opsMap });
  }, [activeConfig, updateMutation]);

  const handleCreateConfig = async () => {
    const name = newConfigName.trim().toLowerCase().replace(/\s+/g, '-');
    if (!name || configNames.includes(name)) return;
    await createMutation.mutateAsync({ name, operations: {} });
    setNewConfigName('');
    setShowNewConfigInput(false);
    setActiveConfig(name);
  };

  const handleDeleteConfig = async (name: string) => {
    if (name === activeDefaultName) return;
    if (name === activeReEvalName) {
      alert('This config is the re-eval config. Unset Re-eval before deleting it.');
      return;
    }
    if (!confirm(`Delete pipeline config "${name}"?`)) return;
    await deleteMutation.mutateAsync(name);
    // Clear selection — the picker effect will choose the active default
    // (or the first remaining config) once the list query refreshes.
    if (activeConfig === name) setActiveConfig('');
  };

  const handleSetAsDefault = async (name: string) => {
    if (!confirm(`Set "${name}" as the default pipeline config? All users without an explicit override will use this config.`)) return;
    await setDefaultMutation.mutateAsync(name);
  };

  const handleSetAsReEval = async (name: string) => {
    if (!confirm(`Set "${name}" as the re-eval pipeline config? All re-evaluations (student & teacher) will run through it.`)) return;
    await setReEvalMutation.mutateAsync(name);
  };

  const handleUnsetReEval = async () => {
    if (!confirm('Clear the re-eval pipeline config? Re-evaluations will fall back to each user\'s normal config.')) return;
    await setReEvalMutation.mutateAsync('');
  };

  const handleCopyConfig = async (sourceName: string) => {
    const copyName = prompt(`New config name (copy of "${sourceName}"):`);
    if (!copyName) return;
    const name = copyName.trim().toLowerCase().replace(/\s+/g, '-');
    if (!name || configNames.includes(name)) return;
    const sourceDoc = sourceName === activeConfig ? activeConfigDoc : await adminApi.getPipelineConfig(sourceName);
    await createMutation.mutateAsync({ name, operations: sourceDoc?.operations || {} });
    setActiveConfig(name);
  };

  const handlePipelineToggle = (opKey: OpKey, pipelineName: string) => {
    const op = operations[opKey];
    if (!op) return;

    const newPipelines = op.pipelines.map(p => {
      if (p.pipelineName !== pipelineName) return p;
      return { ...p, enabled: !p.enabled };
    });

    // Disabling the LAST enabled pipeline is allowed, and turns the operation
    // off. It used to be a silent no-op — the checkbox simply didn't move and
    // said nothing — which left no way to stop an operation short of editing
    // Cosmos. Turning one off is how you iterate on an upstream step without
    // paying for the downstream one (e.g. comparing extraction models without
    // buying enrichment for output nobody reads).
    //
    // It IS a broken configuration for real users — the operation's caller gets
    // no outcome — so the card says so loudly rather than relying on the admin
    // to remember. Not a prod setting.
    const primaryStillEnabled = newPipelines.find(p => p.isPrimary && p.enabled);
    if (!primaryStillEnabled) {
      const firstEnabled = newPipelines.find(p => p.enabled);
      if (firstEnabled) {
        for (const p of newPipelines) p.isPrimary = p.pipelineName === firstEnabled.pipelineName;
      }
    }

    const newOps = { ...operations, [opKey]: { pipelines: newPipelines } };
    setOperations(newOps);
    saveOperations(newOps);
  };

  const handleSetPrimaryPipeline = (opKey: OpKey, pipelineName: string) => {
    const op = operations[opKey];
    if (!op) return;

    const newPipelines = op.pipelines.map(p => ({
      ...p,
      isPrimary: p.pipelineName === pipelineName,
    }));

    const newOps = { ...operations, [opKey]: { pipelines: newPipelines } };
    setOperations(newOps);
    saveOperations(newOps);
  };

  /** Update pipeline-level targeting (typed-pipelines §3.4 / §5).
   *  `axis` is a key of Targeting; `values` is the new value list. An empty
   *  list removes the constraint on that axis (sparse-write — matches the
   *  prompt-targeting convention so admin-cleared checkboxes don't accidentally
   *  exclude every match). */
  const handleSetPipelineTargeting = (
    opKey: OpKey,
    pipelineName: string,
    axis: PipelineTargetingAxis,
    values: string[],
  ) => {
    const op = operations[opKey];
    if (!op) return;
    const newPipelines = op.pipelines.map(p => {
      if (p.pipelineName !== pipelineName) return p;
      const nextTargeting: Targeting = { ...(p.targeting ?? {}) };
      if (values.length === 0) {
        delete (nextTargeting as unknown as Record<string, unknown>)[axis];
      } else if (axis === 'questionTypes') {
        nextTargeting.questionTypes = values as QuestionType[];
      } else if (axis === 'targetExams') {
        nextTargeting.targetExams = values;
      } else if (axis === 'subjects') {
        nextTargeting.subjects = values;
      } else if (axis === 'classes') {
        nextTargeting.classes = values;
      } else {
        nextTargeting.executionModes = values as ('interactive' | 'background')[];
      }
      const isEmpty = Object.keys(nextTargeting).length === 0;
      // Drop the field entirely when no axis is constrained — sparse-write
      // matches the prompt-targeting convention so admin-cleared chips
      // don't leave `targeting: {}` lingering in Cosmos.
      const { targeting: _stripped, ...rest } = p;
      return isEmpty ? rest : { ...rest, targeting: nextTargeting };
    });
    const newOps = { ...operations, [opKey]: { pipelines: newPipelines } };
    setOperations(newOps);
    saveOperations(newOps);
  };

  const handleAddRun = (opKey: OpKey, pipelineName: string, stepType: StepType, promptId: string, target: AddRunProvider) => {
    const op = operations[opKey];
    if (!op) return;

    const newRunBase = (): StepRunConfig => {
      if (target.kind === 'tier') {
        return {
          id: crypto.randomUUID(),
          promptId,
          isPrimary: false,
          providerSet: target.providerSet,
          // Default to the middle tier when present (index 1 mirrors the
          // legacy `'Tier2'` baseline). The runtime clamps to set length.
          defaultTier: 1,
          maxEscalations: 1,
        };
      }
      if (target.kind === 'parallel') {
        return {
          id: crypto.randomUUID(),
          promptId,
          isPrimary: false,
          parallelSet: target.parallelSet,
        };
      }
      return {
        id: crypto.randomUUID(),
        promptId,
        isPrimary: false,
        provider: target.provider,
        temperature: target.temperature,
        maxTokens: target.maxTokens,
        reasoningEffort: target.reasoningEffort,
        thinkingBudgetTokens: target.thinkingBudgetTokens,
        reasoningSummary: target.reasoningSummary,
        useStrictJsonSchema: target.useStrictJsonSchema,
      };
    };

    const newPipelines = op.pipelines.map(p => {
      if (p.pipelineName !== pipelineName) return p;
      return {
        ...p,
        steps: p.steps.map(s => {
          if (s.stepType !== stepType) return s;
          if (opKey === 'realtimeVoice' || opKey === 'learningMoments' || opKey === 'learningMomentsSitting') {
            // Single-run ops only support flat mode (analysis-only tier routing in v1).
            if (target.kind !== 'flat') return s;
            return { ...s, runs: [{ ...newRunBase(), isPrimary: true }] };
          }
          // Conflict detection: same prompt + same provider/set already present
          const dup = s.runs.some(r =>
            r.promptId === promptId && (
              (target.kind === 'flat' && r.provider === target.provider) ||
              (target.kind === 'tier' && r.providerSet === target.providerSet) ||
              (target.kind === 'parallel' && r.parallelSet === target.parallelSet)
            )
          );
          if (dup) return s;
          const isFirst = s.runs.length === 0;
          return {
            ...s,
            runs: [...s.runs, { ...newRunBase(), isPrimary: isFirst }],
          };
        }),
      };
    });

    const newOps = { ...operations, [opKey]: { pipelines: newPipelines } };
    setOperations(newOps);
    saveOperations(newOps);
  };

  const handleRemoveRun = (opKey: OpKey, pipelineName: string, stepType: StepType, runId: string, promptId: string, provider: string) => {
    const op = operations[opKey];
    if (!op) return;

    const newPipelines = op.pipelines.map(p => {
      if (p.pipelineName !== pipelineName) return p;
      return {
        ...p,
        steps: p.steps.map(s => {
          if (s.stepType !== stepType) return s;
          const newRuns = s.runs.filter(r => !matchesRun(r, runId, promptId, provider));
          if (newRuns.length > 0 && !newRuns.some(r => r.isPrimary)) {
            newRuns[0].isPrimary = true;
          }
          return { ...s, runs: newRuns };
        }),
      };
    });

    const newOps = { ...operations, [opKey]: { pipelines: newPipelines } };
    setOperations(newOps);
    saveOperations(newOps);
  };

  const handleSetPrimaryRun = (opKey: OpKey, pipelineName: string, stepType: StepType, runId: string, promptId: string, provider: string) => {
    const op = operations[opKey];
    if (!op) return;

    const newPipelines = op.pipelines.map(p => {
      if (p.pipelineName !== pipelineName) return p;
      return {
        ...p,
        steps: p.steps.map(s => {
          if (s.stepType !== stepType) return s;
          return {
            ...s,
            runs: s.runs.map(r => ({
              ...r,
              isPrimary: matchesRun(r, runId, promptId, provider),
            })),
          };
        }),
      };
    });

    const newOps = { ...operations, [opKey]: { pipelines: newPipelines } };
    setOperations(newOps);
    saveOperations(newOps);
  };

  /** Gate a parallel run's other branches behind a feature gate (or clear
   *  it): off, the set's default branch reads alone. See
   *  reconciled-readings-target.md §2.2. */
  const handleSetParallelGate = (opKey: OpKey, pipelineName: string, stepType: StepType, runId: string, promptId: string, provider: string, gate: string | undefined) => {
    const op = operations[opKey];
    if (!op) return;
    const newPipelines = op.pipelines.map(p => {
      if (p.pipelineName !== pipelineName) return p;
      return {
        ...p,
        steps: p.steps.map(s => {
          if (s.stepType !== stepType) return s;
          return {
            ...s,
            runs: s.runs.map(r => {
              if (!matchesRun(r, runId, promptId, provider)) return r;
              const { parallelGate: _parallelGate, ...rest } = r;
              return gate ? { ...rest, parallelGate: gate } : rest;
            }),
          };
        }),
      };
    });
    const newOps = { ...operations, [opKey]: { pipelines: newPipelines } };
    setOperations(newOps);
    saveOperations(newOps);
  };

  const handleUpdateRunParams = (opKey: OpKey, pipelineName: string, stepType: StepType, runId: string, promptId: string, provider: string, params: { temperature?: number; maxTokens?: number; timeoutMs?: number }) => {
    const op = operations[opKey];
    if (!op) return;
    const newPipelines = op.pipelines.map(p => {
      if (p.pipelineName !== pipelineName) return p;
      return {
        ...p,
        steps: p.steps.map(s => {
          if (s.stepType !== stepType) return s;
          return {
            ...s,
            runs: s.runs.map(r => {
              if (!matchesRun(r, runId, promptId, provider)) return r;
              return { ...r, ...params };
            }),
          };
        }),
      };
    });
    const newOps = { ...operations, [opKey]: { pipelines: newPipelines } };
    setOperations(newOps);
    saveOperations(newOps);
  };

  /** Edit a run — supports changing prompt and/or provider and switching
   *  between flat, tier-aware, and parallel modes in addition to tweaking
   *  params. Atomic single save. The mode is selected by which of
   *  `params.providerSet` / `params.parallelSet` is set; otherwise flat. The
   *  unused-mode fields are cleared so the backend mode-mixing validator
   *  passes. */
  const handleEditRun = (
    opKey: OpKey,
    pipelineName: string,
    stepType: StepType,
    runId: string,
    oldPromptId: string,
    oldProvider: string,
    newPromptId: string,
    newProvider: string,
    params: EditRunParams
  ) => {
    const tierAware = !!params.providerSet;
    const parallel = !!params.parallelSet;
    const applyParams = (run: StepRunConfig): StepRunConfig => applyRunParams(run, params);
    const op = operations[opKey];
    if (!op) return;
    const newPipelines = op.pipelines.map(p => {
      if (p.pipelineName !== pipelineName) return p;
      return {
        ...p,
        steps: p.steps.map(s => {
          if (s.stepType !== stepType) return s;
          // Identity unchanged → just patch params (mode-aware via applyParams)
          if (oldPromptId === newPromptId && oldProvider === newProvider) {
            return {
              ...s,
              runs: s.runs.map(r =>
                matchesRun(r, runId, oldPromptId, oldProvider) ? applyParams(r) : r
              ),
            };
          }
          // Identity changed → ensure no conflict, swap in place preserving isPrimary and order
          const conflict = s.runs.some(r => r.promptId === newPromptId && (r.provider ?? '') === newProvider && !matchesRun(r, runId, oldPromptId, oldProvider));
          if (conflict) {
            return s; // Skip change if target combo already exists
          }
          return {
            ...s,
            runs: s.runs.map(r => {
              if (!matchesRun(r, runId, oldPromptId, oldProvider)) return r;
              const patched = applyParams(r);
              const providerOverride = (tierAware || parallel) ? {} : { provider: newProvider };
              return { ...patched, promptId: newPromptId, ...providerOverride };
            }),
          };
        }),
      };
    });
    const newOps = { ...operations, [opKey]: { pipelines: newPipelines } };
    setOperations(newOps);
    saveOperations(newOps);
  };

  const toggleSection = (key: string) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const getPromptById = (promptId: string): PromptInfo | undefined => {
    return prompts?.find((p: PromptInfo) => p.id === promptId);
  };

  /** Pipeline templates per operation. Each template fixes a `pipelineName`
   *  (which is also the runner-registry key) and a step list. The Add
   *  Pipeline UI is a dropdown over this map — admins can't hand-craft a
   *  step sequence; pipeline shapes are first-class.
   *
   *  Typed-pipeline shapes (post Step 4 of typed-pipelines.md) end with a
   *  `score_*` compute step. Legacy shapes (`direct`, `extract-then-analyze`)
   *  are kept until the migration retires them. */
  interface PipelineTemplate {
    pipelineName: string;
    displayName: string;
    steps: StepType[];
  }
  const PIPELINE_TEMPLATES: Record<OpKey, PipelineTemplate[]> = {
    analysis: [
      { pipelineName: 'direct',                                displayName: 'Direct (legacy)',                              steps: ['analyze_image_text_objective'] },
      { pipelineName: 'direct-objective',                      displayName: 'Direct — Objective (typed)',                   steps: ['analyze_image_text_objective', 'score_objective'] },
      { pipelineName: 'direct-freeform',                       displayName: 'Direct — Freeform (typed)',                    steps: ['analyze_image_text_freeform', 'score_freeform'] },
      { pipelineName: 'extract-then-analyze',                  displayName: 'Extract → Analyze (legacy)',                   steps: ['extract_steps', 'analyze_text_objective'] },
      { pipelineName: 'extract-then-analyze-objective',        displayName: 'Extract → Analyze — Objective (typed)',        steps: ['extract_steps', 'analyze_text_objective', 'score_objective'] },
      { pipelineName: 'extract-then-analyze-freeform',         displayName: 'Extract → Analyze — Freeform (typed)',         steps: ['extract_steps', 'analyze_text_freeform', 'score_freeform'] },
      { pipelineName: 'extract-visuals-then-analyze-objective',displayName: 'Extract Visuals → Analyze — Objective (typed)',steps: ['extract_visuals', 'analyze_visual_objective', 'score_objective'] },
      { pipelineName: 'extract-visuals-then-analyze-freeform', displayName: 'Extract Visuals → Analyze — Freeform (typed)', steps: ['extract_visuals', 'analyze_visual_freeform', 'score_freeform'] },
      { pipelineName: 'analyze-descriptive',                   displayName: 'Analyze — Descriptive (typed, no extraction)',  steps: ['analyze_descriptive', 'score_descriptive'] },
    ],
    hint: [
      { pipelineName: 'hint-default',              displayName: 'Direct Hint',                     steps: ['hint_for_image_text'] },
      { pipelineName: 'extract-then-hint',         displayName: 'Extract → Hint',                  steps: ['extract_steps', 'hint_for_text'] },
      { pipelineName: 'extract-visuals-then-hint', displayName: 'Extract Visuals → Hint (Visual)', steps: ['extract_visuals', 'hint_for_visual'] },
    ],
    textchat: [
      { pipelineName: 'textchat-default', displayName: 'TextChat (Greet → Guide → Summarize)', steps: ['textchat_greet', 'textchat_guide', 'textchat_summarize'] },
    ],
    conceptLab: [
      { pipelineName: 'concept-lab-default', displayName: 'Concept Lab', steps: ['concept_lab'] },
    ],
    realtimeVoice: [
      { pipelineName: 'voice-default', displayName: 'Voice (Realtime)', steps: ['voice_realtime'] },
    ],
    learningMoments: [
      { pipelineName: 'learning-moments-default', displayName: 'Learning Moments', steps: ['detect_learning_moments'] },
    ],
    // One call per sitting instead of one per question — the candidate
    // catalogue sent once, every analysed question laid out together. Which of
    // the two runs for a student is the learning_moment_sitting_detect gate.
    // See docs/FeatureDesigns/learning-moments/detection-control-vision.md §3.
    learningMomentsSitting: [
      { pipelineName: 'learning-moments-sitting-default', displayName: 'Learning Moments (whole sitting)', steps: ['detect_learning_moments_sitting'] },
    ],
    testReport: [
      { pipelineName: 'test-report-default', displayName: 'Test Report', steps: ['generate_test_report'] },
    ],
    chemistryLabMix: [
      { pipelineName: 'chemistry-lab-mix-default', displayName: 'Chemistry Lab — Mix', steps: ['chemistry_lab_mix'] },
    ],
    chemistryLabExcess: [
      { pipelineName: 'chemistry-lab-excess-default', displayName: 'Chemistry Lab — Excess', steps: ['chemistry_lab_excess'] },
    ],
    // No template offered: the compile runs from the batch tooling, not from a
    // pipeline an admin starts here. Empty rather than a guessed default — a
    // template nobody can run is a button that only ever fails.
    compileScene: [],
    // One template list per judge: they are separate operations because every
    // enabled pipeline in an operation runs and the primary supplies the
    // result, so offering both under one operation hands each caller a chance
    // of the other's output.
    judgeAnswerEquiv: [
      { pipelineName: 'judge-answer-equivalence', displayName: 'Judge — Answer Equivalence', steps: ['eval_judge_answer_equivalence'] },
    ],
    judgeQuality: [
      { pipelineName: 'judge-assessment-quality', displayName: 'Judge — Assessment Quality', steps: ['eval_judge_assessment_quality'] },
    ],
    generateVariations: [
      { pipelineName: 'generate-variations-default', displayName: 'Generate Variations (PDF)', steps: ['generate_variations'] },
    ],
    generateVariationsFromQuestions: [
      { pipelineName: 'variations-from-questions-default', displayName: 'Generate Variations (from questions)', steps: ['generate_variations_from_questions'] },
    ],
    // The optional `validate_extraction` judge is NOT a template variant here.
    // Every enabled pipeline in an operation runs — so a "with judge" twin of a
    // pipeline that already exists would extract the paper twice, not add a
    // check to it. Add the step to the existing pipeline instead (+ Add Step).
    // See docs/FeatureDesigns/extraction-validation-judge.md.
    //
    // The multi-part variants below are NOT twins in that sense — they read the
    // same paper under a different contract (their prompt returns `parts[]`), and
    // exactly one of the two should run per import. That is what makes TARGETING
    // mandatory on both: set questionTypes to STRUCTURED on the multi-part
    // pipeline and FREEFORM on the flat one, or every multi-part import extracts
    // the paper twice. The extraction job stamps that shape on its synthetic
    // question from the teacher's "this paper has multi-part questions" tick.
    // See docs/FeatureDesigns/assignments-tests/structured-question-extraction-target.md.
    extractVerbatim: [
      { pipelineName: 'extract-verbatim-default', displayName: 'Extract Verbatim (PDF)', steps: ['extract_verbatim'] },
      { pipelineName: 'extract-verbatim-structured', displayName: 'Extract Verbatim — Multi-Part (PDF)', steps: ['extract_verbatim_structured'] },
    ],
    // DOCX (text) sibling of extractVerbatim.
    extractVerbatimText: [
      { pipelineName: 'extract-verbatim-text-default', displayName: 'Extract Verbatim (Word)', steps: ['extract_verbatim_text'] },
      { pipelineName: 'extract-verbatim-text-structured', displayName: 'Extract Verbatim — Multi-Part (Word)', steps: ['extract_verbatim_text_structured'] },
    ],
    // Second pass over each extracted question — authors solutions/hints/answer.
    enrichQuestions: [
      { pipelineName: 'enrich-questions-default', displayName: 'Enrich Extracted Questions', steps: ['enrich_questions'] },
    ],
    splitSolutions: [
      { pipelineName: 'split-solutions-default', displayName: 'Split Solutions', steps: ['split_solutions'] },
    ],
    // The final answer only, off the uncropped page, for observation.
    // See docs/FeatureDesigns/solution-analysis/answer-second-read-target.md.
    readAnswer: [
      { pipelineName: 'read-answer-default', displayName: 'Answer Second Read', steps: ['read_answer'] },
    ],
    // Each uploaded solution page read once, beside the splitter, with no question
    // context.
    // See docs/FeatureDesigns/solution-analysis/solution-page-reader-target.md.
    // A page marked as an answer list, read alone beside the splitter; behind
    // solution_answer_list_step.
    // See docs/FeatureDesigns/assignments-tests/answer-sheet-reader-target.md §4.
    readAnswerList: [
      { pipelineName: 'read-answer-list-default', displayName: 'Answer List Read', steps: ['read_answer_list'] },
    ],
    readSolutionPages: [
      { pipelineName: 'read-solution-pages-default', displayName: 'Solution Page Reader', steps: ['read_solution_pages'] },
    ],
    // Mark-scheme document → per-question credit rubrics. One call; the join +
    // tariff cross-check that follows it is deterministic code, not a step.
    // See docs/FeatureDesigns/AssessmentAndMarking/marking-scheme-upload.md.
    extractMarkScheme: [
      { pipelineName: 'extract-mark-scheme-default', displayName: 'Extract Mark Scheme', steps: ['extract_mark_scheme'] },
    ],
    // A teacher's separate answer & solution key → rows by question number. One
    // call per page chunk (or one for a Word key); matching rows to the paper's
    // questions is deterministic code, not a step.
    // See docs/FeatureDesigns/assignments-tests/paper-answer-key-target.md.
    extractAnswerKey: [
      { pipelineName: 'extract-answer-key-default', displayName: 'Extract Answer Key', steps: ['extract_answer_key'] },
    ],
    // Whole mark scheme + whole completed attempt → per-question credit verdicts.
    // One call; the marks themselves are computed by applyCreditRubric afterwards,
    // which is deterministic code, not a step.
    // See docs/FeatureDesigns/AssessmentAndMarking/one-shot-marking.md.
    markAttempt: [
      { pipelineName: 'mark-attempt-one-shot', displayName: 'Mark Attempt (one-shot)', steps: ['mark_attempt_one_shot'] },
    ],
    // Per-question-set session report synthesised from the attempt's digests.
    questionSessionReport: [
      { pipelineName: 'question-session-report-default', displayName: 'Question Session Report', steps: ['generate_question_session_report'] },
    ],
    clusterMisconceptions: [
      // Name matches the seed script's pipeline; the runner it resolves to is
      // registered in genericPipelineRunner.ts.
      { pipelineName: 'cluster-misconceptions', displayName: 'Cluster Cohort Misconceptions', steps: ['cluster_misconceptions'] },
    ],
  };

  /** Add a new pipeline instance from a template. The template's
   *  `pipelineName` field is reinterpreted as the new instance's
   *  `pipelineType` (the runner key). The instance's own `pipelineName` is
   *  the same by default, but is auto-suffixed (`-2`, `-3`, …) when an
   *  existing instance already uses that name — so admins can have multiple
   *  instances of the same type (e.g. one targeting interactive, one
   *  background) and tell them apart by name. */
  const handleAddPipeline = (opKey: OpKey, pipelineType: string) => {
    const op = operations[opKey] || { pipelines: [] };

    const template = PIPELINE_TEMPLATES[opKey].find(t => t.pipelineName === pipelineType);
    if (!template) return;

    // Auto-suffix to keep pipelineName unique within the operation.
    const existingNames = new Set(op.pipelines.map(p => p.pipelineName));
    let pipelineName = pipelineType;
    if (existingNames.has(pipelineName)) {
      let n = 2;
      while (existingNames.has(`${pipelineType}-${n}`)) n++;
      pipelineName = `${pipelineType}-${n}`;
    }

    const steps = template.steps.map(stepType => ({ id: crypto.randomUUID(), stepType, runs: [] }));

    const newPipeline = {
      pipelineType,
      pipelineName,
      enabled: true,
      isPrimary: op.pipelines.length === 0,
      steps,
    };

    const newOps = { ...operations, [opKey]: { pipelines: [...op.pipelines, newPipeline] } };
    setOperations(newOps);
    saveOperations(newOps);
  };

  /**
   * Append a step to an EXISTING pipeline.
   *
   * The data model always allowed this — `steps` is just an ordered array — but
   * the console could only ever produce steps from a template at pipeline
   * creation time, so adding one to a live pipeline meant a seed script. That
   * gap is also what makes "with judge" template twins tempting, and those are
   * wrong: every enabled pipeline runs, so a twin duplicates the work instead of
   * checking it.
   *
   * Appended, never inserted: the walker runs steps in order and threads each
   * one's output to the next, so a step that arrives before the one it consumes
   * has nothing to read. Reordering is deliberately not offered here.
   */
  const handleAddStep = (opKey: OpKey, pipelineName: string, stepType: StepType) => {
    const op = operations[opKey];
    if (!op) return;
    const newOps = {
      ...operations,
      [opKey]: {
        pipelines: op.pipelines.map(p => (
          p.pipelineName !== pipelineName || p.steps.some(s => s.stepType === stepType)
            ? p
            : { ...p, steps: [...p.steps, { id: crypto.randomUUID(), stepType, runs: [] }] }
        )),
      },
    };
    setOperations(newOps);
    saveOperations(newOps);
  };

  /** Remove a step and every run configured on it. For an optional step (the
   *  extraction judge) this IS the off switch, which is why it needs to be as
   *  reachable as adding it. */
  const handleRemoveStep = (opKey: OpKey, pipelineName: string, stepType: StepType) => {
    const op = operations[opKey];
    if (!op) return;
    const newOps = {
      ...operations,
      [opKey]: {
        pipelines: op.pipelines.map(p => (
          p.pipelineName !== pipelineName
            ? p
            : { ...p, steps: p.steps.filter(s => s.stepType !== stepType) }
        )),
      },
    };
    setOperations(newOps);
    saveOperations(newOps);
  };

  /** Toggle a step on/off in place. `enabled:false` makes the walker skip it (it
   *  records a skipped model-run row) and run the rest of the pipeline — the in-place
   *  off switch for a step you want to A/B without deleting it. Saves immediately,
   *  like add/remove step. */
  const handleToggleStep = (opKey: OpKey, pipelineName: string, stepType: StepType, enabled: boolean) => {
    const op = operations[opKey];
    if (!op) return;
    const newOps = {
      ...operations,
      [opKey]: {
        pipelines: op.pipelines.map(p => (
          p.pipelineName !== pipelineName
            ? p
            : { ...p, steps: p.steps.map(s => s.stepType !== stepType ? s : { ...s, enabled }) }
        )),
      },
    };
    setOperations(newOps);
    saveOperations(newOps);
  };

  /** Step types this operation allows that the pipeline doesn't already carry.
   *  Sourced from the shared OPERATION_STEP_TYPES registry — the same authority
   *  `isStepValidForOp` uses, so the menu can never offer a step that save-time
   *  validation would then reject. */
  const addableSteps = (opKey: OpKey, pipeline: { steps: { stepType: StepType }[] }): StepType[] => {
    const present = new Set(pipeline.steps.map(s => s.stepType));
    return (OPERATION_STEP_TYPES[opKey as OperationType] ?? [])
      .filter((s): s is StepType => !present.has(s as StepType));
  };

  /** Open the theme-aware copy dialog. Suggests `<source>-copy` (auto-
   *  suffixed if taken) as the default name; the admin can edit before
   *  confirming. The actual clone is performed by `commitCopyPipeline`. */
  const handleCopyPipeline = (opKey: OpKey, sourcePipelineName: string) => {
    const op = operations[opKey];
    if (!op) return;

    const existingNames = new Set(op.pipelines.map(p => p.pipelineName));
    const baseName = `${sourcePipelineName}-copy`;
    let suggestedName = baseName;
    let n = 2;
    while (existingNames.has(suggestedName)) {
      suggestedName = `${baseName}-${n}`;
      n++;
    }

    setCopyDialog({ opKey, sourcePipelineName, suggestedName });
  };

  /** Commit the dialog's chosen name. Deep-clones steps + runs with fresh
   *  per-instance ids and inserts a disabled / non-primary pipeline so the
   *  admin can retarget before activating. The dialog has already
   *  validated uniqueness against existingNames. */
  const commitCopyPipeline = (copyName: string) => {
    if (!copyDialog) return;
    const { opKey, sourcePipelineName } = copyDialog;
    const op = operations[opKey];
    if (!op) { setCopyDialog(null); return; }
    const source = op.pipelines.find(p => p.pipelineName === sourcePipelineName);
    if (!source) { setCopyDialog(null); return; }

    const clonedSteps = source.steps.map(step => ({
      ...step,
      id: crypto.randomUUID(),
      runs: (step.runs ?? []).map(run => ({
        ...run,
        id: crypto.randomUUID(),
      })),
    }));

    const clone = {
      ...source,
      pipelineName: copyName,
      enabled: false,
      isPrimary: false,
      steps: clonedSteps,
    };

    const newOps = { ...operations, [opKey]: { pipelines: [...op.pipelines, clone] } };
    setOperations(newOps);
    saveOperations(newOps);
    setCopyDialog(null);
  };

  const handleRemovePipeline = (opKey: OpKey, pipelineName: string) => {
    const op = operations[opKey];
    if (!op) return;
    const newPipelines = op.pipelines.filter(p => p.pipelineName !== pipelineName);
    // Ensure at least one primary if any remain
    if (newPipelines.length > 0 && !newPipelines.some(p => p.isPrimary)) {
      newPipelines[0].isPrimary = true;
    }
    const newOps = { ...operations, [opKey]: { pipelines: newPipelines } };
    setOperations(newOps);
    saveOperations(newOps);
  };

  /** Step types are pinned to their declared prompt type. Image-vs-text
   *  and objective-vs-freeform are part of the prompt's identity, and the
   *  runtime filter in runLLMStep is strict (`p.type === stepType`), so
   *  the admin UI must not offer cross-type matches. */
  const STEP_COMPATIBLE_PROMPT_TYPES: Record<string, StepType[]> = {
    hint_for_image_text:           ['hint_for_image_text'],
    hint_for_text:                 ['hint_for_text'],
    hint_for_visual:               ['hint_for_visual'],
    analyze_image_text_objective:  ['analyze_image_text_objective'],
    analyze_image_text_freeform:   ['analyze_image_text_freeform'],
    analyze_text_objective:        ['analyze_text_objective'],
    analyze_text_freeform:         ['analyze_text_freeform'],
  };

  /** A step filed under an operation that cannot run it — always a config error,
   *  and the prompt picker would offer prompts for the wrong family.
   *
   *  Judged against the shared OPERATION_STEP_TYPES registry, NOT against
   *  PIPELINE_TEMPLATES: the templates are a menu of suggested pipelines and
   *  lag reality — they omit the structured-analysis steps the live default
   *  config has been running in production, so using them here flagged working
   *  pipelines as broken. */
  const isStepValidForOp = (opKey: OpKey, stepType: StepType): boolean =>
    isStepTypeForOperation(opKey, stepType);

  const getPromptsForStep = (stepType: StepType): PromptInfo[] => {
    if (!prompts) return [];
    const allowed = new Set<StepType>(STEP_COMPATIBLE_PROMPT_TYPES[stepType] ?? [stepType]);
    // Type-compatibility is the meaningful constraint here. We deliberately
    // do NOT filter by `settings.activePromptIds` — that field is a UI-side
    // convention for the admin Prompts list. The runtime loads whatever
    // promptIds the pipeline config references (`collectPromptIds` ⇒
    // `loadActivePrompts`), so any type-compatible prompt is a valid pick.
    return prompts.filter((p: PromptInfo) => allowed.has(p.type as StepType));
  };

  return (
    <div className="settings-section">
      <div className="section-header">
        <h2>Pipeline Configuration</h2>
        <p className="section-description">
          Configure how each operation runs. Create named configs and assign them to users via their <code>pipelineConfig</code> field.
        </p>
      </div>

      {/* Config selector */}
      <div className="pipeline-config-selector">
        <div className="pipeline-config-tabs">
          {configNames.map(name => (
            <button
              key={name}
              className={`pipeline-config-tab ${activeConfig === name ? 'active' : ''} ${name === activeDefaultName ? 'is-default' : ''}`}
              onClick={() => setActiveConfig(name)}
            >
              {name}
              {name === activeDefaultName && <span className="default-badge">default</span>}
              {name === activeReEvalName && <span className="reeval-badge">re-eval</span>}
            </button>
          ))}
          {showNewConfigInput ? (
            <span className="new-config-inline">
              <input
                type="text"
                value={newConfigName}
                onChange={e => setNewConfigName(e.target.value)}
                placeholder="config-name"
                autoFocus
                onKeyDown={e => { if (e.key === 'Enter') handleCreateConfig(); if (e.key === 'Escape') setShowNewConfigInput(false); }}
              />
              <Button variant="primary" size="sm" onClick={handleCreateConfig} disabled={!newConfigName.trim()}>Create</Button>
              <Button variant="secondary" size="sm" onClick={() => setShowNewConfigInput(false)}>Cancel</Button>
            </span>
          ) : (
            <button className="pipeline-config-tab add-config" onClick={() => setShowNewConfigInput(true)}>+</button>
          )}
        </div>
        {activeConfig && (
          <div className="pipeline-config-actions">
            {activeConfig !== activeDefaultName && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleSetAsDefault(activeConfig)}
                disabled={setDefaultMutation.isPending}
              >
                Set as Default
              </Button>
            )}
            {activeConfig !== activeReEvalName ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleSetAsReEval(activeConfig)}
                disabled={setReEvalMutation.isPending}
              >
                Set as Re-eval
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={handleUnsetReEval}
                disabled={setReEvalMutation.isPending}
              >
                Unset Re-eval
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setShowHistory(true)}>History</Button>
            <Button variant="outline" size="sm" onClick={() => handleCopyConfig(activeConfig)}>Duplicate</Button>
            {activeConfig !== activeDefaultName && (
              <Button variant="danger" size="sm" onClick={() => handleDeleteConfig(activeConfig)}>Delete</Button>
            )}
          </div>
        )}
      </div>

      {showHistory && activeConfig && (
        <ConfigHistoryDrawer
          configName={activeConfig}
          onClose={() => setShowHistory(false)}
          onRestored={() => {
            queryClient.invalidateQueries({ queryKey: ['pipelineConfig', activeConfig] });
            queryClient.invalidateQueries({ queryKey: ['pipelineConfigs'] });
          }}
        />
      )}

      {/* Operation selector — pipelines per operation are independent.
          Show only the selected one to keep the page focused. */}
      <div className="pipeline-op-selector">
        {ALL_OP_KEYS.map(opKey => {
          const count = operations[opKey]?.pipelines.length ?? 0;
          return (
            <button
              key={opKey}
              className={`pipeline-op-tab ${selectedOp === opKey ? 'active' : ''}`}
              onClick={() => {
                setSelectedOp(opKey);
                setExpandedSections(prev => ({ ...prev, [opKey]: true }));
              }}
            >
              {OP_LABELS[opKey]}
              {count > 0 && <span className="pipeline-op-count">{count}</span>}
            </button>
          );
        })}
      </div>

      {[selectedOp].map(opKey => {
        const singleRun = OP_SINGLE_RUN[opKey];
        const pipelines = operations[opKey]?.pipelines || [];

        return (
          <div key={opKey} className="pipeline-operation-section">
            <div className="pipeline-operation-header" onClick={() => toggleSection(opKey)}>
              <h3>{OP_LABELS[opKey]}</h3>
              <span className="collapse-icon">{expandedSections[opKey] ? '\u25BC' : '\u25B6'}</span>
            </div>

            {expandedSections[opKey] && operations[opKey] && (
              <div className="pipeline-operation-content">
                <p className="section-description" style={{ marginBottom: '12px' }}>
                  {OP_DESCRIPTIONS[opKey]}
                </p>
                <div className="pipeline-types">
                  <label className="pipeline-types-label">Pipelines</label>
                  {pipelines.length === 0 && (
                    <div style={{ marginBottom: '8px' }}>
                      <p className="no-runs-message" style={{ marginBottom: '8px' }}>No pipelines configured yet.</p>
                      <Button
                        variant="primary"
                        size="sm"
                        disabled={mutation.isPending}
                        // Build from the operation's OWN first template. This used to be
                        // a hardcoded stepType ladder covering only the seven original
                        // operations, with `analyze_image_text_objective` as the else —
                        // so every operation added since (question session report,
                        // generate variations, extract verbatim, enrich, split solutions)
                        // got an ANALYSIS step, and the prompt picker then correctly
                        // offered analysis prompts for it.
                        onClick={() => handleAddPipeline(opKey, PIPELINE_TEMPLATES[opKey][0].pipelineName)}
                      >
                        Add Default Pipeline
                      </Button>
                    </div>
                  )}
                  {/* Every pipeline off = the operation is off. Legitimate while
                      iterating on an upstream step, broken for real users — the
                      caller gets no outcome — so it is stated plainly rather
                      than inferred from unchecked boxes. */}
                  {pipelines.length > 0 && !pipelines.some(p => p.enabled) && (
                    <div className="operation-disabled-banner">
                      <strong>{OP_LABELS[opKey]} is turned off.</strong>{' '}
                      Every pipeline here is disabled, so anything that runs this operation gets no
                      result. Intended for development — re-enable a pipeline before this config is
                      used by real users.
                    </div>
                  )}
                  <div className="pipeline-type-list">
                    {pipelines.map(pipeline => (
                        <div key={pipeline.pipelineName} className={`pipeline-type-card ${pipeline.enabled ? 'enabled' : ''} ${pipeline.isPrimary ? 'primary' : ''}`}>
                          <div className="pipeline-type-header">
                            <label className="pipeline-type-toggle">
                              <input
                                type="checkbox"
                                checked={pipeline.enabled}
                                onChange={() => handlePipelineToggle(opKey, pipeline.pipelineName)}
                                disabled={mutation.isPending}
                              />
                              <span className="pipeline-type-name">{formatPipelineName(pipeline.pipelineName)}</span>
                            </label>
                            <div className="pipeline-header-actions">
                              {pipeline.enabled && pipelines.length > 1 && (
                                <label className="pipeline-primary-toggle" title="Set as primary pipeline">
                                  <input
                                    type="radio"
                                    name={`${activeConfig}-${opKey}-primary-pipeline`}
                                    checked={pipeline.isPrimary}
                                    onChange={() => handleSetPrimaryPipeline(opKey, pipeline.pipelineName)}
                                    disabled={mutation.isPending}
                                  />
                                  <span className="primary-label">Primary</span>
                                </label>
                              )}
                              <button
                                className="copy-pipeline-btn"
                                onClick={() => handleCopyPipeline(opKey, pipeline.pipelineName)}
                                disabled={mutation.isPending}
                                title="Duplicate this pipeline (creates a disabled copy you can retarget)"
                              >
                                Copy
                              </button>
                              <button
                                className="remove-pipeline-btn"
                                onClick={() => { if (confirm(`Remove pipeline "${pipeline.pipelineName}" from ${OP_LABELS[opKey]}?`)) handleRemovePipeline(opKey, pipeline.pipelineName); }}
                                disabled={mutation.isPending}
                                title="Remove pipeline"
                              >
                                &times;
                              </button>
                            </div>
                          </div>

                          {pipeline.enabled && (
                            <PipelineTargetingEditor
                              targeting={pipeline.targeting}
                              disabled={mutation.isPending}
                              onChange={(axis, values) => handleSetPipelineTargeting(opKey, pipeline.pipelineName, axis, values)}
                            />
                          )}

                          {pipeline.enabled && (
                            <div className="pipeline-steps">
                              {pipeline.steps.map((step, stepIdx) => (
                                <StepConfigEditor
                                  key={step.stepType}
                                  step={step}
                                  stepIndex={stepIdx}
                                  totalSteps={pipeline.steps.length}
                                  availablePrompts={getPromptsForStep(step.stepType)}
                                  invalidForOp={!isStepValidForOp(opKey, step.stepType)}
                                  opLabel={OP_LABELS[opKey]}
                                  enabledProviders={allProviderKeys}
                                  providerConfigs={providerConfigs}
                                  getPromptById={getPromptById}
                                  isPending={mutation.isPending}
                                  singleRun={singleRun}
                                  onAddRun={(promptId, target) => handleAddRun(opKey, pipeline.pipelineName, step.stepType, promptId, target)}
                                  onRemoveRun={(runId, promptId, provider) => handleRemoveRun(opKey, pipeline.pipelineName, step.stepType, runId, promptId, provider)}
                                  onSetPrimary={(runId, promptId, provider) => handleSetPrimaryRun(opKey, pipeline.pipelineName, step.stepType, runId, promptId, provider)}
                                  onUpdateRunParams={(runId, promptId, provider, params) => handleUpdateRunParams(opKey, pipeline.pipelineName, step.stepType, runId, promptId, provider, params)}
                                  onSetParallelGate={(runId, promptId, provider, gate) => handleSetParallelGate(opKey, pipeline.pipelineName, step.stepType, runId, promptId, provider, gate)}
                                  onEditRun={(runId, oldPromptId, oldProvider, newPromptId, newProvider, params) => handleEditRun(opKey, pipeline.pipelineName, step.stepType, runId, oldPromptId, oldProvider, newPromptId, newProvider, params)}
                                  providerSets={providerSets}
                                  onNavigate={onNavigate}
                                  parallelSets={parallelSets}
                                  onRemoveStep={() => handleRemoveStep(opKey, pipeline.pipelineName, step.stepType)}
                                  onToggleEnabled={(enabled) => handleToggleStep(opKey, pipeline.pipelineName, step.stepType, enabled)}
                                />
                              ))}
                              {/* Add a step to THIS pipeline. Only step types the
                                  operation allows and the pipeline lacks — so the
                                  menu can never build a config that save-time
                                  validation rejects. Appended at the end, because
                                  the walker runs steps in order and threads each
                                  one's output into the next. */}
                              {addableSteps(opKey, pipeline).length > 0 && (
                                <div className="add-step-row">
                                  <select
                                    className="add-step-select"
                                    value=""
                                    disabled={mutation.isPending}
                                    onChange={e => {
                                      if (e.target.value) handleAddStep(opKey, pipeline.pipelineName, e.target.value as StepType);
                                      e.target.value = '';
                                    }}
                                  >
                                    <option value="">+ Add Step…</option>
                                    {addableSteps(opKey, pipeline).map(st => (
                                      <option key={st} value={st}>{promptTypeLabel(st) || st}</option>
                                    ))}
                                  </select>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                  {/* Add Pipeline button */}
                  <div className="add-pipeline-row">
                    {addingPipelineFor === opKey ? (
                      <span className="new-pipeline-inline">
                        {(() => {
                          const existingNames = new Set(pipelines.map(p => p.pipelineName));
                          const templates = PIPELINE_TEMPLATES[opKey] ?? [];
                          const commit = () => {
                            if (!selectedTemplate) return;
                            handleAddPipeline(opKey, selectedTemplate);
                            setSelectedTemplate('');
                            setAddingPipelineFor(null);
                          };
                          return (
                            <>
                              <select
                                value={selectedTemplate}
                                onChange={e => setSelectedTemplate(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Escape') { setAddingPipelineFor(null); setSelectedTemplate(''); } }}
                                autoFocus
                              >
                                <option value="">Select pipeline type…</option>
                                {templates.map(t => {
                                  const taken = existingNames.has(t.pipelineName);
                                  return (
                                    <option key={t.pipelineName} value={t.pipelineName} disabled={taken}>
                                      {t.displayName}{taken ? ' (already added)' : ''}
                                    </option>
                                  );
                                })}
                              </select>
                              <Button variant="primary" size="sm" onClick={commit} disabled={!selectedTemplate}>Add</Button>
                              <Button variant="secondary" size="sm" onClick={() => { setAddingPipelineFor(null); setSelectedTemplate(''); }}>Cancel</Button>
                            </>
                          );
                        })()}
                      </span>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setAddingPipelineFor(opKey)}
                        disabled={mutation.isPending}
                      >
                        + Add Pipeline
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {copyDialog && (
        <CopyPipelineDialog
          isOpen={true}
          sourcePipelineName={copyDialog.sourcePipelineName}
          opLabel={OP_LABELS[copyDialog.opKey]}
          existingNames={new Set((operations[copyDialog.opKey]?.pipelines ?? []).map(p => p.pipelineName))}
          suggestedName={copyDialog.suggestedName}
          onClose={() => setCopyDialog(null)}
          onConfirm={commitCopyPipeline}
        />
      )}
    </div>
  );
}

function StepConfigEditor({
  step,
  stepIndex,
  totalSteps,
  availablePrompts,
  invalidForOp,
  opLabel,
  enabledProviders,
  providerConfigs,
  getPromptById,
  isPending,
  onAddRun,
  onRemoveRun,
  onSetPrimary,
  onUpdateRunParams,
  onSetParallelGate,
  onEditRun,
  singleRun,
  providerSets,
  parallelSets,
  onNavigate,
  onRemoveStep,
  onToggleEnabled,
}: {
  step: PipelineStepConfig;
  stepIndex: number;
  totalSteps: number;
  availablePrompts: PromptInfo[];
  /** This step type doesn't belong to the operation it's filed under. */
  invalidForOp?: boolean;
  opLabel?: string;
  enabledProviders: string[];
  providerConfigs: ProviderConfig[];
  getPromptById: (id: string) => PromptInfo | undefined;
  isPending: boolean;
  onAddRun: (promptId: string, target: AddRunProvider) => void;
  onRemoveRun: (runId: string, promptId: string, provider: string) => void;
  onSetPrimary: (runId: string, promptId: string, provider: string) => void;
  onUpdateRunParams: (runId: string, promptId: string, provider: string, params: { temperature?: number; maxTokens?: number; timeoutMs?: number }) => void;
  onSetParallelGate?: (runId: string, promptId: string, provider: string, gate: string | undefined) => void;
  onEditRun?: (runId: string, oldPromptId: string, oldProvider: string, newPromptId: string, newProvider: string, params: EditRunParams) => void;
  singleRun?: boolean;
  providerSets: ProviderTieredSet[];
  parallelSets: ProviderParallelSet[];
  onNavigate?: (tab: ProviderNavTarget, focus?: string) => void;
  /** Drop this step and its runs. For an optional step this is the off switch,
   *  so it sits next to the step rather than behind a script. */
  onRemoveStep?: () => void;
  /** Toggle this step on/off in place (walker skips it when off) without deleting it. */
  onToggleEnabled?: (enabled: boolean) => void;
}) {
  const stepEnabled = step.enabled !== false;
  const getProviderLabel = (key: string) => providerConfigs.find(p => p.key === key)?.displayName || key;
  // Single editor state used by both Add and Edit modal flows.
  type EditorMode = { kind: 'add' } | { kind: 'edit'; runId: string; originalPromptId: string; originalProvider: string };
  const [editor, setEditor] = useState<EditorMode | null>(null);
  type ProviderMode = 'flat' | 'tier' | 'parallel';
  const [rowDraft, setRowDraft] = useState<{
    promptId?: string;
    provider?: string;
    temperature?: number;
    maxTokens?: number;
    reasoningEffort?: ReasoningEffort;
    thinkingBudgetTokens?: number;
    reasoningSummary?: ReasoningSummaryMode;
    useStrictJsonSchema?: boolean;
    /** Per-call timeout for the step, ms; undefined ⇒ the global default. */
    timeoutMs?: number;
    /** Provider mode — exactly one of flat / tier / parallel is active. */
    mode?: ProviderMode;
    // Tier-aware mode
    providerSet?: string;
    /** 0-indexed tier reference; clamped to the chosen set's length at runtime. */
    defaultTier?: number;
    maxEscalations?: number;
    // Parallel mode
    parallelSet?: string;
  }>({});

  const stepLabel = step.stepType.charAt(0).toUpperCase() + step.stepType.slice(1);
  const isComputeStep = stepTypeFamily(step.stepType) === 'compute';
  /** Steps a reconcile step can follow: a parallel run here is two readings
   *  of one thing, and its other branches can be gated (the cross-check). */
  const isReadingStep = Object.values(RECONCILE_STEP_SOURCES).some(sources => sources?.includes(step.stepType));

  /** Preferred default provider when adding a new run. Falls back to the
   *  first enabled provider if the preferred one isn't available. */
  const PREFERRED_DEFAULT_PROVIDER = 'gpt-4.1';
  const defaultProviderForAdd = enabledProviders.includes(PREFERRED_DEFAULT_PROVIDER)
    ? PREFERRED_DEFAULT_PROVIDER
    : enabledProviders[0] || '';

  const openAddModal = () => {
    setEditor({ kind: 'add' });
    setRowDraft({
      promptId: '',
      provider: defaultProviderForAdd,
      // Param fields intentionally left undefined — unset means "inherit from
      // the selected provider's config at runtime" (see mergeProviderDefaults
      // in callProvider.ts).
      mode: 'flat',
      providerSet: providerSets[0]?.name || '',
      defaultTier: 1,
      maxEscalations: 1,
      parallelSet: parallelSets[0]?.name || '',
    });
  };

  const startEdit = (run: StepRunConfig) => {
    setEditor({ kind: 'edit', runId: run.id ?? '', originalPromptId: run.promptId, originalProvider: run.provider ?? '' });
    const mode: ProviderMode = run.parallelSet ? 'parallel' : run.providerSet ? 'tier' : 'flat';
    setRowDraft({
      promptId: run.promptId,
      provider: run.provider ?? (enabledProviders[0] || ''),
      temperature: run.temperature,
      maxTokens: run.maxTokens,
      reasoningEffort: run.reasoningEffort,
      thinkingBudgetTokens: run.thinkingBudgetTokens,
      reasoningSummary: run.reasoningSummary,
      useStrictJsonSchema: run.useStrictJsonSchema,
      timeoutMs: run.timeoutMs,
      mode,
      providerSet: run.providerSet ?? (providerSets[0]?.name || ''),
      defaultTier: coerceDefaultTier(run.defaultTier),
      maxEscalations: run.maxEscalations ?? 1,
      parallelSet: run.parallelSet ?? (parallelSets[0]?.name || ''),
    });
  };

  const closeEditor = () => {
    setEditor(null);
    setRowDraft({});
  };

  const saveEditor = () => {
    if (!editor || !rowDraft.promptId) return;
    const mode: ProviderMode = rowDraft.mode ?? 'flat';
    if (editor.kind === 'add') {
      if (mode === 'tier') {
        if (!rowDraft.providerSet) return;
        onAddRun(rowDraft.promptId, { kind: 'tier', providerSet: rowDraft.providerSet });
      } else if (mode === 'parallel') {
        if (!rowDraft.parallelSet) return;
        onAddRun(rowDraft.promptId, { kind: 'parallel', parallelSet: rowDraft.parallelSet });
      } else {
        if (!rowDraft.provider) return;
        onAddRun(rowDraft.promptId, {
          kind: 'flat',
          provider: rowDraft.provider,
          temperature: rowDraft.temperature,
          maxTokens: rowDraft.maxTokens,
          reasoningEffort: rowDraft.reasoningEffort,
          thinkingBudgetTokens: rowDraft.thinkingBudgetTokens,
          reasoningSummary: rowDraft.reasoningSummary,
          useStrictJsonSchema: rowDraft.useStrictJsonSchema,
        });
      }
      // Note: defaults set on add are sufficient — to tweak temp/tier/etc., user opens Edit afterwards.
      closeEditor();
      return;
    }
    // Edit mode
    const oldPromptId = editor.originalPromptId;
    const oldProvider = editor.originalProvider;
    const newPromptId = rowDraft.promptId;
    const newProvider = rowDraft.provider ?? oldProvider;
    let params: EditRunParams;
    if (mode === 'tier') {
      params = {
        providerSet: rowDraft.providerSet,
        defaultTier: rowDraft.defaultTier ?? 1,
        maxEscalations: rowDraft.maxEscalations ?? 1,
        // Per-run values state the STEP's requirement and override each tier's
        // provider entry; unset inherits it.
        temperature: rowDraft.temperature,
        maxTokens: rowDraft.maxTokens,
        timeoutMs: rowDraft.timeoutMs,
      };
    } else if (mode === 'parallel') {
      params = { parallelSet: rowDraft.parallelSet, temperature: rowDraft.temperature, maxTokens: rowDraft.maxTokens, timeoutMs: rowDraft.timeoutMs };
    } else {
      params = {
        temperature: rowDraft.temperature,
        maxTokens: rowDraft.maxTokens,
        reasoningEffort: rowDraft.reasoningEffort,
        thinkingBudgetTokens: rowDraft.thinkingBudgetTokens,
        reasoningSummary: rowDraft.reasoningSummary,
        useStrictJsonSchema: rowDraft.useStrictJsonSchema,
        timeoutMs: rowDraft.timeoutMs,
      };
    }
    const runId = editor.runId;
    if (onEditRun) {
      onEditRun(runId, oldPromptId, oldProvider, newPromptId, newProvider, params);
    } else {
      onUpdateRunParams(runId, oldPromptId, oldProvider, { temperature: rowDraft.temperature, maxTokens: rowDraft.maxTokens, timeoutMs: rowDraft.timeoutMs });
    }
    closeEditor();
  };

  /** Per-call timeout — every provider mode, edit only. Sits above the
   *  "Providers used" list in tier and parallel modes, with the step's own
   *  settings rather than after the resolved providers. */
  const timeoutField = editor?.kind === 'edit' ? (
    <label className="step-editor-field">
      <span>Timeout (s)</span>
      <input
        type="number"
        min={10}
        step={1}
        value={rowDraft.timeoutMs != null ? Math.round(rowDraft.timeoutMs / 1000) : ''}
        onChange={e => {
          const sec = parseInt(e.target.value, 10);
          setRowDraft({ ...rowDraft, timeoutMs: Number.isFinite(sec) && sec > 0 ? sec * 1000 : undefined });
        }}
        placeholder={`default: ${DEFAULT_CALL_TIMEOUT_S}`}
        title="Per-call timeout for this step, every provider mode. Empty uses the default (120 s). Raise it only for a step that is slow by design — not to hide a hung provider — and keep it under the job's own time limit."
        disabled={isPending}
      />
    </label>
  ) : null;

  const currentRun = singleRun ? step.runs[0] : undefined;
  const currentPrompt = currentRun ? getPromptById(currentRun.promptId) : undefined;
  const currentProviderLabel = currentRun ? getProviderLabel((currentRun.provider ?? '')) : undefined;

  // In-place on/off switch: when off, the walker skips this step (recording a skipped
  // model-run) and runs the rest of the pipeline. Lets an admin A/B a step (e.g.
  // extract_visuals) without deleting it or duplicating the pipeline.
  const stepEnableToggle = onToggleEnabled && (
    <label
      className={`step-enable-toggle${stepEnabled ? '' : ' step-enable-toggle--off'}`}
      title={stepEnabled ? 'Step runs — click to disable (walker will skip it)' : 'Step disabled — the walker skips it'}
    >
      <input
        type="checkbox"
        checked={stepEnabled}
        disabled={isPending}
        onChange={(e) => onToggleEnabled(e.target.checked)}
      />
      <span>{stepEnabled ? 'Enabled' : 'Disabled'}</span>
    </label>
  );

  if (isComputeStep) {
    // Compute steps (score_*) have no prompt, no provider, no temperature.
    // The runtime invokes them as pure functions over the prior step's
    // outcomes — see typed-pipelines.md §6. Render a plain row so the
    // pipeline shape is visible without exposing controls that don't apply.
    return (
      <div className={`pipeline-step${stepEnabled ? '' : ' pipeline-step--disabled'}`}>
        <div className="pipeline-step-header">
          <span className="step-badge">Step {stepIndex + 1}/{totalSteps}</span>
          <span className="step-type-label">{promptTypeLabel(step.stepType)}</span>
          <span className="step-kind-badge" title="Compute step — runs in-process; no prompt or provider">compute</span>
          {stepEnableToggle}
        </div>
      </div>
    );
  }

  return (
    <div className={`pipeline-step${invalidForOp ? ' pipeline-step--invalid' : ''}${stepEnabled ? '' : ' pipeline-step--disabled'}`}>
      <div className="pipeline-step-header">
        <span className="step-badge">Step {stepIndex + 1}/{totalSteps}</span>
        <span className="step-type-label">{stepLabel}</span>
        {stepEnableToggle}
        {invalidForOp && <span className="step-invalid-badge" title="This step type is not part of this operation">not a {opLabel} step</span>}
        {/* Only the LAST step is removable. The walker threads each step's
            output into the next, so pulling one from the middle silently
            starves everything downstream of its input. */}
        {onRemoveStep && stepIndex === totalSteps - 1 && totalSteps > 1 && (
          <button
            type="button"
            className="step-remove-btn"
            disabled={isPending}
            title="Remove this step and its runs"
            onClick={() => onRemoveStep()}
          >
            ×
          </button>
        )}
      </div>
      {invalidForOp && (
        <p className="step-invalid-note">
          <code>{step.stepType}</code> isn&apos;t one of {opLabel}&apos;s step types, so this pipeline
          can&apos;t run and the prompts below would be the wrong family. Remove this pipeline and add
          one from the templates.
        </p>
      )}

      {singleRun ? (
        <>
          {currentRun && (
            <div className="single-run-current">
              <div className="single-run-header">
                Active: <strong>{currentPrompt?.name || currentRun.promptId}</strong> <span className="version-tag">v{currentPrompt?.version}</span> &middot; <span className="provider-key">{currentProviderLabel || (currentRun.provider ?? '')}</span>
              </div>
              {(() => {
                const prov = providerConfigs.find(p => p.key === currentRun.provider);
                const caps = prov ? getProviderCapabilities(prov.family, prov.modelName) : null;
                return (
                  <div className="step-params-row">
                    {caps?.temperature && (
                      <label className="step-param">
                        <span>Temperature</span>
                        <NumberInput
                          allowEmpty
                          value={currentRun.temperature}
                          // 0 is a temperature, not "unset": `|| undefined` used to drop it.
                          onChange={v => onUpdateRunParams(currentRun.id ?? '', currentRun.promptId, (currentRun.provider ?? ''), { temperature: v ?? undefined })}
                          min={0} max={2} step={0.1}
                          placeholder={prov?.temperature !== undefined ? `inherits: ${prov.temperature}` : 'not set'}
                          className="step-param-input"
                        />
                      </label>
                    )}
                    {caps?.maxTokens && (
                      <label className="step-param">
                        <span>Max Tokens</span>
                        <IntegerInput
                          allowEmpty
                          value={currentRun.maxTokens}
                          onChange={v => onUpdateRunParams(currentRun.id ?? '', currentRun.promptId, (currentRun.provider ?? ''), { maxTokens: v ?? undefined })}
                          min={100} max={65536} step={256}
                          placeholder={prov?.maxTokens !== undefined ? `inherits: ${prov.maxTokens}` : 'not set'}
                          className="step-param-input"
                        />
                      </label>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
          {(
            <Button
              variant="outline"
              size="sm"
              onClick={openAddModal}
              disabled={isPending}
            >
              {currentRun ? '+ Change Prompt' : '+ Add Prompt'}
            </Button>
          )}
        </>
      ) : (
        <>
          {step.runs.length > 0 ? (
            <div className="dt-container--bordered">
              <table className="dt dt--compact">
                <thead>
                  <tr>
                    <th>Primary</th>
                    {isReadingStep && <th title="A parallel run's other branches run only when this gate is on; off, the default branch reads alone">Other branches</th>}
                    <th>Prompt</th>
                    <th>Target Exams</th>
                    <th>Question Types</th>
                    <th>Provider</th>
                    <th>Properties</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {step.runs.map((run) => {
                    const rowKey = `${run.promptId}|${run.provider ?? ''}|${run.providerSet ?? ''}|${run.parallelSet ?? ''}`;
                    const prompt = getPromptById(run.promptId);
                    const providerLabel = getProviderLabel(run.provider ?? '');
                    const qTypes = prompt?.targeting?.questionTypes;
                    const exams = prompt?.targeting?.targetExams;
                    const isTier = !!run.providerSet;
                    const isParallel = !!run.parallelSet;
                    return (
                      <tr key={rowKey} className={run.isPrimary ? 'dt-row--highlight' : ''}>
                        <td>
                          <input
                            type="radio"
                            name={`primary-${step.stepType}`}
                            checked={run.isPrimary || false}
                            onChange={() => onSetPrimary(run.id ?? '', run.promptId, run.provider ?? '')}
                            disabled={isPending}
                          />
                        </td>
                        {isReadingStep && (
                          <td>
                            {(isParallel || isTier) && (
                              <select
                                value={run.parallelGate ?? ''}
                                title="Feature gate the parallel set's other branches run behind — always: every branch reads"
                                onChange={e => onSetParallelGate?.(run.id ?? '', run.promptId, run.provider ?? '', e.target.value || undefined)}
                                disabled={isPending || !onSetParallelGate}
                              >
                                <option value="">always</option>
                                {FEATURE_GATE_NAMES.map(g => <option key={g} value={g}>{g}</option>)}
                              </select>
                            )}
                          </td>
                        )}
                        <td>
                          {prompt?.name || run.promptId} <span className="version-tag">v{prompt?.version}</span>
                        </td>
                        <td className="target-exams-cell">{exams?.length ? exams.join(', ') : 'All'}</td>
                        <td className="question-types-cell">{qTypes?.length ? qTypes.join(', ') : 'All'}</td>
                        <td>
                          {isTier ? (
                            <>
                              <ProviderLink
                                className="provider-set-name"
                                title="Tier-aware (analysis tier routing) — open Tiered Sets"
                                onNavigate={onNavigate && (() => onNavigate('tiered-sets', run.providerSet))}
                              >{run.providerSet}</ProviderLink>
                              {/* The set name alone doesn't say which models run — and the
                                  reachable tiers depend on this run's tier + escalations. */}
                              <ResolvedProviderHint
                                providers={resolveTierProviders(
                                  providerSets.find(s => s.name === run.providerSet),
                                  coerceDefaultTier(run.defaultTier),
                                  run.maxEscalations ?? 1,
                                  parallelSets,
                                  providerConfigs,
                                )}
                              />
                            </>
                          ) : isParallel ? (
                            <>
                              <ProviderLink
                                className="provider-set-name"
                                title="Parallel fan-out (no tier escalation) — open Parallel Sets"
                                onNavigate={onNavigate && (() => onNavigate('parallel-sets', run.parallelSet))}
                              >
                                {run.parallelSet} <em style={{ opacity: 0.6, fontStyle: 'normal', fontSize: '0.85em' }}>(parallel)</em>
                              </ProviderLink>
                              <ResolvedProviderHint
                                providers={resolveParallelProviders(
                                  parallelSets.find(s => s.name === run.parallelSet),
                                  providerConfigs,
                                )}
                              />
                            </>
                          ) : (
                            <ProviderLink
                              className="provider-key"
                              title={`Open ${run.provider} in Providers`}
                              onNavigate={onNavigate && (() => onNavigate('providers', run.provider))}
                            >{providerLabel}</ProviderLink>
                          )}
                        </td>
                        <td className="properties-cell">
                          {isTier ? (
                            <>
                              <span className="prop-pair"><span className="prop-label">Tier</span> <span className="prop-value">{tierLabel(coerceDefaultTier(run.defaultTier))}</span></span>
                              <span className="prop-pair"><span className="prop-label">MaxEsc</span> <span className="prop-value">{run.maxEscalations ?? 1}</span></span>
                              <span className="prop-pair"><span className="prop-label">Temp</span> {run.temperature !== undefined
                                ? <span className="prop-value">{run.temperature}</span>
                                : <span className="prop-value" style={{ opacity: 0.35 }} title="Inherits each tier's provider default">—</span>}</span>
                              <span className="prop-pair"><span className="prop-label">Tokens</span> {run.maxTokens !== undefined
                                ? <span className="prop-value">{run.maxTokens}</span>
                                : <span className="prop-value" style={{ opacity: 0.35 }} title="Inherits each tier's provider default">—</span>}</span>
                            </>
                          ) : isParallel ? (() => {
                            const ps = parallelSets.find(s => s.name === run.parallelSet);
                            const branchCount = ps?.branches?.length ?? 0;
                            const selection = ps?.selection;
                            // For 'default' selection, surface the winning
                            // branch's provider so admins don't have to open
                            // the parallel-sets page to see which branch is
                            // primary. Other strategies (e.g. 'best-result')
                            // pick at runtime, so we show the strategy name.
                            const selectionDisplay = selection === 'default' && ps?.defaultBranch
                              ? getProviderLabel(ps.defaultBranch)
                              : (selection ?? '—');
                            const selectionTitle = selection === 'default' && ps?.defaultBranch
                              ? `Default branch: ${ps.defaultBranch}`
                              : undefined;
                            return (
                              <>
                                <span className="prop-pair"><span className="prop-label">Branches</span> <span className="prop-value">{branchCount}</span></span>
                                <span className="prop-pair"><span className="prop-label">Selection</span> <span className="prop-value" title={selectionTitle}>{selectionDisplay}</span></span>
                                <span className="prop-pair"><span className="prop-label">Tokens</span> {run.maxTokens !== undefined
                                  ? <span className="prop-value">{run.maxTokens}</span>
                                  : <span className="prop-value" style={{ opacity: 0.35 }} title="Inherits each branch's provider default">—</span>}</span>
                              </>
                            );
                          })() : (() => {
                            const prov = providerConfigs.find(p => p.key === run.provider);
                            const caps = prov ? getProviderCapabilities(prov.family, prov.modelName) : null;
                            const inherited = (val: unknown, provVal: unknown) =>
                              val !== undefined
                                ? <span className="prop-value">{String(val)}</span>
                                : provVal !== undefined
                                  ? <span className="prop-value" style={{ opacity: 0.5, fontStyle: 'italic' }}>{String(provVal)}</span>
                                  : <span className="prop-value" style={{ opacity: 0.35 }}>—</span>;
                            return (
                              <>
                                {caps?.temperature && (
                                  <span className="prop-pair"><span className="prop-label">Temp</span> {inherited(run.temperature, prov?.temperature)}</span>
                                )}
                                {caps?.maxTokens && (
                                  <span className="prop-pair"><span className="prop-label">Tokens</span> {inherited(run.maxTokens, prov?.maxTokens)}</span>
                                )}
                                {caps?.reasoningEffort && (
                                  <span className="prop-pair"><span className="prop-label">Effort</span> {inherited(run.reasoningEffort, prov?.reasoningEffort)}</span>
                                )}
                                {caps?.thinkingBudgetTokens && (
                                  <span className="prop-pair"><span className="prop-label">Think</span> {inherited(run.thinkingBudgetTokens, prov?.thinkingBudgetTokens)}</span>
                                )}
                              </>
                            );
                          })()}
                          {/* Every mode: a step's own timeout, or the default it falls back to. */}
                          <span className="prop-pair"><span className="prop-label">Timeout</span> {run.timeoutMs != null
                            ? <span className="prop-value">{Math.round(run.timeoutMs / 1000)}s</span>
                            : <span className="prop-value" style={{ opacity: 0.35 }} title="The global per-call default">{DEFAULT_CALL_TIMEOUT_S}s</span>}</span>
                        </td>
                        <td>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => startEdit(run)}
                            disabled={isPending}
                          >
                            Edit
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="no-runs-message">No prompt+provider combinations configured.</p>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={openAddModal}
            disabled={isPending}
          >
            + Add Prompt + Provider
          </Button>
        </>
      )}

      {editor && (
        <Modal
          isOpen={true}
          onClose={closeEditor}
          title={editor.kind === 'add' ? `Add ${stepLabel} run` : `Edit ${stepLabel} run`}
          size="md"
        >
          <div className="step-editor-form">
            <label className="step-editor-field">
              <span>Prompt</span>
              <select
                value={rowDraft.promptId ?? ''}
                onChange={e => setRowDraft({ ...rowDraft, promptId: e.target.value })}
                disabled={isPending}
              >
                <option value="">Select prompt…</option>
                {(invalidForOp ? [] : availablePrompts).map((p: PromptInfo) => (
                  <option key={p.id} value={p.id}>{p.name} (v{p.version})</option>
                ))}
              </select>
            </label>

            {!singleRun && (
              <div className="step-editor-field">
                <span>Provider Mode</span>
                <div className="step-editor-mode-row" role="radiogroup" aria-label="Provider mode">
                  {(['flat', 'tier', 'parallel'] as const).map(m => (
                    <label key={m}>
                      <input
                        type="radio"
                        name={`provider-mode-${step.stepType}`}
                        checked={(rowDraft.mode ?? 'flat') === m}
                        onChange={() => setRowDraft({ ...rowDraft, mode: m })}
                        disabled={isPending}
                      />
                      <span>{m === 'flat' ? 'Flat' : m === 'tier' ? 'Tier-aware' : 'Parallel'}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {rowDraft.mode === 'parallel' ? (
              <>
                <label className="step-editor-field">
                  <span>Parallel Set</span>
                  <select
                    value={rowDraft.parallelSet ?? ''}
                    onChange={e => setRowDraft({ ...rowDraft, parallelSet: e.target.value })}
                    disabled={isPending}
                  >
                    <option value="">Select set…</option>
                    {parallelSets.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
                  </select>
                </label>
                <label className="step-editor-field">
                  <span>Temperature</span>
                  <input
                    type="number"
                    min={0}
                    max={2}
                    step={0.1}
                    value={rowDraft.temperature ?? ''}
                    onChange={e => setRowDraft({ ...rowDraft, temperature: e.target.value === '' ? undefined : parseFloat(e.target.value) })}
                    placeholder="inherits each branch's provider default"
                    disabled={isPending}
                  />
                </label>
                <label className="step-editor-field">
                  <span>Max Tokens</span>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={rowDraft.maxTokens ?? ''}
                    onChange={e => setRowDraft({ ...rowDraft, maxTokens: parseInt(e.target.value, 10) || undefined })}
                    placeholder="inherits each branch's provider default"
                    disabled={isPending}
                  />
                </label>
                {timeoutField}
                <ResolvedProviderList
                  providers={resolveParallelProviders(
                    parallelSets.find(s => s.name === rowDraft.parallelSet),
                    providerConfigs,
                  )}
                  emptyHint="Pick a parallel set to see its branches."
                />
              </>
            ) : rowDraft.mode === 'tier' ? (
              <>
                <label className="step-editor-field">
                  <span>Provider Set</span>
                  <select
                    value={rowDraft.providerSet ?? ''}
                    onChange={e => setRowDraft({ ...rowDraft, providerSet: e.target.value })}
                    disabled={isPending}
                  >
                    <option value="">Select set…</option>
                    {providerSets.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
                  </select>
                </label>
                <div className="step-editor-row">
                  <label className="step-editor-field">
                    <span>Default Tier</span>
                    {(() => {
                      // Tier options come from the chosen set's `levels` length —
                      // both the set count and the indices are dynamic.
                      const selectedSet = providerSets.find(s => s.name === rowDraft.providerSet);
                      const tierCount = selectedSet?.levels?.length ?? 0;
                      const draftTier = rowDraft.defaultTier ?? 1;
                      const clampedTier = tierCount > 0 ? Math.min(draftTier, tierCount - 1) : draftTier;
                      return (
                        <select
                          value={clampedTier}
                          onChange={e => setRowDraft({ ...rowDraft, defaultTier: parseInt(e.target.value, 10) })}
                          disabled={isPending || tierCount === 0}
                        >
                          {tierCount === 0 ? (
                            <option value={0}>(pick a provider set)</option>
                          ) : (
                            Array.from({ length: tierCount }, (_, i) => (
                              <option key={i} value={i}>{tierLabel(i)}</option>
                            ))
                          )}
                        </select>
                      );
                    })()}
                  </label>
                  <label className="step-editor-field">
                    <span>Max Escalations</span>
                    <IntegerInput
                      min={0}
                      step={1}
                      value={rowDraft.maxEscalations ?? 1}
                      onChange={v => { if (v != null) setRowDraft({ ...rowDraft, maxEscalations: v }); }}
                      disabled={isPending}
                    />
                  </label>
                  <label className="step-editor-field">
                    <span>Temperature</span>
                    <input
                      type="number"
                      min={0}
                      max={2}
                      step={0.1}
                      value={rowDraft.temperature ?? ''}
                      onChange={e => setRowDraft({ ...rowDraft, temperature: e.target.value === '' ? undefined : parseFloat(e.target.value) })}
                      placeholder="inherits each tier's provider default"
                      disabled={isPending}
                    />
                  </label>
                  <label className="step-editor-field">
                    <span>Max Tokens</span>
                    <input
                      type="number"
                      min={1}
                      step={1}
                      value={rowDraft.maxTokens ?? ''}
                      onChange={e => setRowDraft({ ...rowDraft, maxTokens: parseInt(e.target.value, 10) || undefined })}
                      placeholder="inherits each tier's provider default"
                      disabled={isPending}
                    />
                  </label>
                </div>
                {timeoutField && <div className="step-editor-row">{timeoutField}</div>}
                <ResolvedProviderList
                  providers={resolveTierProviders(
                    providerSets.find(s => s.name === rowDraft.providerSet),
                    rowDraft.defaultTier ?? 1,
                    rowDraft.maxEscalations ?? 1,
                    parallelSets,
                    providerConfigs,
                  )}
                  emptyHint="Pick a provider set to see the tiers this run can reach."
                />
              </>
            ) : (() => {
              const selectedProv = providerConfigs.find(p => p.key === rowDraft.provider);
              const caps = selectedProv
                ? getProviderCapabilities(selectedProv.family, selectedProv.modelName)
                : null;
              return (
                <>
                  <label className="step-editor-field">
                    <span>Provider</span>
                    <select
                      value={rowDraft.provider ?? ''}
                      onChange={e => setRowDraft({
                        ...rowDraft,
                        provider: e.target.value,
                        // Switching provider wipes any per-run overrides — the
                        // old values were tied to the old provider's defaults.
                        // The new provider's defaults show up as placeholders.
                        temperature: undefined,
                        maxTokens: undefined,
                        reasoningEffort: undefined,
                        thinkingBudgetTokens: undefined,
                        reasoningSummary: undefined,
                        useStrictJsonSchema: undefined,
                      })}
                      disabled={isPending}
                    >
                      <option value="">Select provider…</option>
                      {enabledProviders.map(p => (
                        <option key={p} value={p}>{getProviderLabel(p)}</option>
                      ))}
                    </select>
                  </label>
                  {caps && (
                    <div className="step-editor-row" style={{ flexWrap: 'wrap' }}>
                      {caps.temperature && (
                        <label className="step-editor-field">
                          <span>Temperature</span>
                          <input
                            type="number"
                            min={0}
                            max={2}
                            step={0.1}
                            value={rowDraft.temperature ?? ''}
                            placeholder={selectedProv?.temperature !== undefined ? `inherits: ${selectedProv.temperature}` : 'not set'}
                            onChange={e => setRowDraft({ ...rowDraft, temperature: e.target.value === '' ? undefined : parseFloat(e.target.value) })}
                            disabled={isPending}
                          />
                        </label>
                      )}
                      {caps.maxTokens && (
                        <label className="step-editor-field">
                          <span>Max Tokens</span>
                          <input
                            type="number"
                            min={100}
                            max={65536}
                            step={256}
                            value={rowDraft.maxTokens ?? ''}
                            placeholder={selectedProv?.maxTokens !== undefined ? `inherits: ${selectedProv.maxTokens}` : 'not set'}
                            onChange={e => setRowDraft({ ...rowDraft, maxTokens: e.target.value === '' ? undefined : parseInt(e.target.value, 10) })}
                            disabled={isPending}
                          />
                        </label>
                      )}
                      {caps.reasoningEffort && (
                        <label className="step-editor-field">
                          <span>Reasoning Effort</span>
                          <select
                            value={rowDraft.reasoningEffort ?? ''}
                            onChange={e => setRowDraft({ ...rowDraft, reasoningEffort: e.target.value === '' ? undefined : e.target.value as ReasoningEffort })}
                            disabled={isPending}
                          >
                            <option value="">inherits: {selectedProv?.reasoningEffort ?? 'high'}</option>
                            <option value="minimal">Minimal</option>
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                          </select>
                        </label>
                      )}
                      {caps.thinkingBudgetTokens && (
                        <label className="step-editor-field">
                          <span>Thinking Budget (tokens)</span>
                          <input
                            type="number"
                            min={0}
                            step={1024}
                            value={rowDraft.thinkingBudgetTokens ?? ''}
                            placeholder={selectedProv?.thinkingBudgetTokens !== undefined ? `inherits: ${selectedProv.thinkingBudgetTokens}` : 'not set'}
                            onChange={e => setRowDraft({ ...rowDraft, thinkingBudgetTokens: e.target.value === '' ? undefined : parseInt(e.target.value, 10) })}
                            disabled={isPending}
                          />
                        </label>
                      )}
                      {caps.reasoningSummary && (
                        <label className="step-editor-field">
                          <span>Reasoning Summary</span>
                          <select
                            value={rowDraft.reasoningSummary ?? ''}
                            onChange={e => setRowDraft({ ...rowDraft, reasoningSummary: e.target.value === '' ? undefined : e.target.value as ReasoningSummaryMode })}
                            disabled={isPending}
                          >
                            <option value="">inherits: {selectedProv?.reasoningSummary ?? 'auto'}</option>
                            <option value="none">None</option>
                            <option value="auto">Auto</option>
                            <option value="concise">Concise</option>
                            <option value="detailed">Detailed</option>
                          </select>
                        </label>
                      )}
                      {caps.useStrictJsonSchema && (
                        <label className="step-editor-field">
                          <span>Strict JSON Schema</span>
                          <select
                            value={rowDraft.useStrictJsonSchema === undefined ? '' : rowDraft.useStrictJsonSchema ? 'true' : 'false'}
                            onChange={e => setRowDraft({ ...rowDraft, useStrictJsonSchema: e.target.value === '' ? undefined : e.target.value === 'true' })}
                            disabled={isPending}
                          >
                            <option value="">inherits: prompt</option>
                            <option value="true">On</option>
                            <option value="false">Off</option>
                          </select>
                        </label>
                      )}
                    </div>
                  )}
                </>
              );
            })()}

            {editor.kind === 'edit' && (rowDraft.mode ?? 'flat') === 'flat' && (
              <div className="step-editor-row">{timeoutField}</div>
            )}

            <div className="step-editor-actions">
              {editor.kind === 'edit' && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => {
                    if (!confirm('Delete this run?')) return;
                    onRemoveRun(editor.runId, editor.originalPromptId, editor.originalProvider);
                    closeEditor();
                  }}
                  disabled={isPending}
                >
                  Delete
                </Button>
              )}
              <div className="step-editor-actions-right">
                <Button variant="secondary" size="sm" onClick={closeEditor} disabled={isPending}>Cancel</Button>
                {(() => {
                  const mode: ProviderMode = rowDraft.mode ?? 'flat';
                  // maxTokens must resolve to a value at runtime — either on the
                  // run (override) or on the provider config. Only checked in
                  // flat mode; tier-aware and parallel pull params from the
                  // registry entry per branch.
                  const selProv = mode === 'flat'
                    ? providerConfigs.find(p => p.key === rowDraft.provider)
                    : undefined;
                  const selCaps = selProv
                    ? getProviderCapabilities(selProv.family, selProv.modelName)
                    : null;
                  const maxTokensMissing = mode === 'flat'
                    && !!selCaps?.maxTokens
                    && rowDraft.maxTokens === undefined
                    && selProv?.maxTokens === undefined;
                  const modeFieldMissing =
                    mode === 'tier' ? !rowDraft.providerSet :
                    mode === 'parallel' ? !rowDraft.parallelSet :
                    !rowDraft.provider;
                  return (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={saveEditor}
                      disabled={
                        isPending
                        || !rowDraft.promptId
                        || modeFieldMissing
                        || maxTokensMissing
                      }
                      title={maxTokensMissing ? 'Max Tokens must be set on this run or on the provider' : undefined}
                    >
                      {editor.kind === 'add' ? 'Add' : 'Save'}
                    </Button>
                  );
                })()}
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

type PipelineTargetingAxis = 'questionTypes' | 'targetExams' | 'subjects' | 'classes' | 'executionModes';

/**
 * Pipeline-level targeting editor. Surfaces the axes real config uses:
 * questionTypes (the objective/freeform split), targetExams (exam-specific
 * pipelines), subjects and classes (a pipeline for one subject's written
 * answers in a band of grades — the descriptive analyzer), and execution
 * mode. difficultyLevels, userProficiency and moduleIds stay out until a
 * config calls for them.
 *
 * Selection ranks by specificity — the number of axes set — so a pipeline
 * with subjects + classes takes its slice from one with questionTypes alone
 * (typed-pipelines §5). Empty selection on an axis = no constraint
 * (sparse-write).
 */
function PipelineTargetingEditor({
  targeting,
  disabled,
  onChange,
}: {
  targeting: Targeting | undefined;
  disabled: boolean;
  onChange: (axis: PipelineTargetingAxis, values: string[]) => void;
}) {
  const selectedQTypes = new Set(targeting?.questionTypes ?? []);
  const selectedExams = new Set(targeting?.targetExams ?? []);
  const selectedSubjects = new Set(targeting?.subjects ?? []);
  const selectedClasses = new Set(targeting?.classes ?? []);
  const selectedModes = new Set(targeting?.executionModes ?? []);

  const toggle = (axis: PipelineTargetingAxis, value: string) => {
    const current = axis === 'questionTypes' ? selectedQTypes
      : axis === 'targetExams' ? selectedExams
      : axis === 'subjects' ? selectedSubjects
      : axis === 'classes' ? selectedClasses
      : selectedModes;
    const next = new Set(current);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onChange(axis, Array.from(next));
  };

  return (
    <div className="pipeline-targeting">
      <div className="pipeline-targeting-row">
        <span className="pipeline-targeting-label">Question Types</span>
        <div className="pipeline-targeting-chips">
          {QUESTION_TYPES.map(qt => {
            const active = selectedQTypes.has(qt);
            return (
              <button
                key={qt}
                type="button"
                className={`targeting-chip${active ? ' targeting-chip--active' : ''}`}
                onClick={() => toggle('questionTypes', qt)}
                disabled={disabled}
                title={QUESTION_TYPE_LABELS[qt]}
              >
                {qt}
              </button>
            );
          })}
          {selectedQTypes.size === 0 && (
            <span className="targeting-empty-hint">no constraint — matches all question types</span>
          )}
        </div>
      </div>
      <div className="pipeline-targeting-row">
        <span className="pipeline-targeting-label">Target Exams</span>
        <div className="pipeline-targeting-chips">
          {REGISTRABLE_PROGRAMME_OPTIONS.map(exam => {
            const active = selectedExams.has(exam);
            return (
              <button
                key={exam}
                type="button"
                className={`targeting-chip${active ? ' targeting-chip--active' : ''}`}
                onClick={() => toggle('targetExams', exam)}
                disabled={disabled}
              >
                {exam}
              </button>
            );
          })}
          {selectedExams.size === 0 && (
            <span className="targeting-empty-hint">no constraint — matches all exams</span>
          )}
        </div>
      </div>
      <div className="pipeline-targeting-row">
        <span className="pipeline-targeting-label">Subjects</span>
        <div className="pipeline-targeting-chips">
          {QUESTION_SUBJECT_OPTIONS.map(subject => {
            const active = selectedSubjects.has(subject);
            return (
              <button
                key={subject}
                type="button"
                className={`targeting-chip${active ? ' targeting-chip--active' : ''}`}
                onClick={() => toggle('subjects', subject)}
                disabled={disabled}
              >
                {subject}
              </button>
            );
          })}
          {selectedSubjects.size === 0 && (
            <span className="targeting-empty-hint">no constraint — matches all subjects</span>
          )}
        </div>
      </div>
      <div className="pipeline-targeting-row">
        <span className="pipeline-targeting-label" title="The student's class. A pipeline for a band of grades, e.g. 6–8.">Classes</span>
        <div className="pipeline-targeting-chips">
          {GRADE_OPTIONS.map(grade => {
            const active = selectedClasses.has(grade);
            return (
              <button
                key={grade}
                type="button"
                className={`targeting-chip${active ? ' targeting-chip--active' : ''}`}
                onClick={() => toggle('classes', grade)}
                disabled={disabled}
              >
                {grade}
              </button>
            );
          })}
          {selectedClasses.size === 0 && (
            <span className="targeting-empty-hint">no constraint — matches all classes</span>
          )}
        </div>
      </div>
      <div className="pipeline-targeting-row">
        <span className="pipeline-targeting-label" title="Pick which call paths this pipeline applies to. Interactive = live student calls; Background = assignments / off-critical-path jobs.">Execution Mode</span>
        <div className="pipeline-targeting-chips">
          {(['interactive', 'background'] as const).map(mode => {
            const active = selectedModes.has(mode);
            return (
              <button
                key={mode}
                type="button"
                className={`targeting-chip${active ? ' targeting-chip--active' : ''}`}
                onClick={() => toggle('executionModes', mode)}
                disabled={disabled}
              >
                {mode === 'interactive' ? 'Interactive' : 'Background'}
              </button>
            );
          })}
          {selectedModes.size === 0 && (
            <span className="targeting-empty-hint">no constraint — matches both modes</span>
          )}
        </div>
      </div>
    </div>
  );
}
